import { Form, Input } from 'antd';
import FormItemValue from 'components/Form/FormItemValue';
import ServiceBottomSheet, { useServiceBottomSheet } from 'features/booking/BookingOnlineV2/ServiceBottomSheet';
import StaffBottomSheet, { useStaffBottomSheet } from 'features/booking/BookingOnlineV2/StaffBottomSheet';
import { sumBy } from 'lodash';
import moment from 'moment';
import React, { forwardRef } from 'react';
import styled from 'styled-components';
import ic_calendar_outline from './assets/ic_calendar-outline.svg';
import ic_time from './assets/ic_time.svg';
import { CustomerListStyled } from './CustomerServicesStep';
import CustomerServices from './CustomerServicesStep/CustomerServices';
import DepositCancellationPolicy from './Deposit/CancellationPolicy';
import NoticeDeposit from './Deposit/NoticeDeposit';
import { IFormValueServiceItem } from './types';
import RequestInput from './RequestInput';

type ReviewStepRef = {
  fetch: (date: string) => void;
};
export const reviewStepRef = React.createRef<ReviewStepRef>();
const ReviewStep = forwardRef<ReviewStepRef>(() => {
  const staffBs = useStaffBottomSheet();
  const serviceBs = useServiceBottomSheet();

  return (
    <>
      <NoticeDeposit />
      <Container>
        <p className='staff-name'>Review and confirm selections below</p>
        <div className="section-box">
          <div className="customer-box">
            <div className="customer-info">
              <FormItemValue name={'customerName'} render={value => <span className="customer-name">{value}</span>} />
              <FormItemValue name={'customerPhone'} render={value => <span className="customer-phone">{value}</span>} />
            </div>
          </div>
          <Form.Item noStyle name={'note'}>
            <Input.TextArea style={{ background: '#feff9c', fontSize: 16 }} placeholder='Note' />
          </Form.Item>
        </div>
        <div className="section-box">
          <div className="time-box">
            <div className="time-box-item">
              <img src={ic_calendar_outline} alt="ic_calendar_outline" className="icon" />
              <FormItemValue name={'date'} render={value => <span>{value?.format('MM-DD-YYYY')}</span>} />
            </div>
            <div className="time-box-item">
              <img src={ic_time} alt="ic_time" className="icon" />
              <FormItemValue name={'startTime'} render={startTime => <>
                <FormItemValue name={'customers'} render={customers => {
                  const endTime = moment(startTime, 'hh:mm A')?.add(sumBy(customers?.[0]?.services || [], (o: IFormValueServiceItem) => o.duration), 'minutes')?.format('hh:mm A');
                  return <span>{startTime} - {endTime}</span>;
                }} />
              </>} />
            </div>
          </div>
        </div>

        <Form.List name={'customers'}>
          {(fields) => {
            if (fields.length === 1) return (
              <div className="section-box">
                <div className="service-title-box">
                  <label >Services</label>
                  <Form.Item noStyle name={[fields[0].name, 'requestStaff']}>
                    <RequestBox />
                  </Form.Item>
                </div>
                <Form.Item key={fields[0].key} name={fields[0].name} noStyle>
                  <CustomerServices single editName={false} customerLength={fields.length} name={fields[0].name} index={0} serviceBs={serviceBs} staffBs={staffBs} />
                </Form.Item>
              </div>
            );
            return <>
              <CustomerListStyled>
                {
                  fields.map((field, index) => {
                    return <Form.Item key={field.key} name={field.name} noStyle>
                      <CustomerServices editName={false} customerLength={fields.length} name={field.name} index={index} serviceBs={serviceBs} staffBs={staffBs} />
                    </Form.Item>;
                  })
                }
              </CustomerListStyled>
            </>;
          }}
        </Form.List>
      </Container>
      <DepositCancellationPolicy />
      <StaffBottomSheet ref={staffBs} />
      <ServiceBottomSheet ref={serviceBs} />
    </>
  );
});
ReviewStep.displayName = 'ReviewStep';
export default ReviewStep;

type RequestBoxProps = {
  value?: boolean;
  onChange?: (value?: boolean) => void;
};
const RequestBox = ({ value, onChange }: RequestBoxProps) => {
  return (
    <>
      <RequestInput value={value} onClick={() => onChange?.(true)} label='Request' />
      <RequestInput value={!value} onClick={() => onChange?.(false)} label='Non Request' />
    </>
  );
};

const Container = styled.div`
p.staff-name {
  color: #1D2129;
  text-align: center;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}

.section-box {
  display: flex;
  padding: 8px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 4px;
  background: #F6F7FC;
}

.customer-box {
  display: flex;
  padding: 8px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
  border-radius: 4px;
  background: #FFF;
  .customer-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    span.customer-name {
      color: #1D2129;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: 30px; /* 187.5% */
      text-transform: capitalize;
    }

    span.customer-phone {
      color: #1D2129;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }
  }
}

.time-box {
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 4px;
  justify-content: space-between;
  background: #FFF;
  .time-box-item {
    display: flex;
    padding-right: 8px;
    align-items: center;
    gap: 4px;
    &:nth-child(0) {
      border-right: 1px solid #CCD4DC;
    }

    img {
      width: 20px;
      height: 20px;
    }

    span {
      color: #1D2129;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
  }
}


.service-title-box {
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  label {
    flex: 1;
    color: #64748B;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }
}


`;